<template>
  <InnerView>
    <template #header>
      <div>
        <KnowledgeOptions
          :display-ai-action="displayAiAction"
          :ai-collapse-displayed="displayAiCollapse"
          :display-modal-action="false"
          @toggle-ai-collapse="displayAiCollapse = !displayAiCollapse"
        />
        <div
          class="bg-white rounded-lg transition-all overflow-hidden flex-none px-4"
          :class="
            displayAiCollapse
              ? `py-2 mt-2 opacity-100 ${aiCollapseHeight.open}`
              : `${aiCollapseHeight.close} p-0 mt-0 opacity-0`
          "
        >
          <ai-agent-collapse
            v-if="displayAiCollapse"
            :content="content"
            :summary="contentSummary"
            :action-items="contentActionItems"
          />
        </div>
      </div>
    </template>
    <div class="h-full bg-white overflow-y-auto p-4 flex flex-col gap-4 overflow-x-hidden">
      <main-knowledge-breadcrumb-wrapper :custom-width="customWidth" @go-to-entity="goToEntity" />
      <main-content-view
        :config="getConfig()"
        :item-is-favorite="itemIsFavorite"
        :modal-is-open="false"
        @go-to-entity="handleEntity"
        @toggle-favorite="toggleFavorite"
        @go-to-feedback="goToFeedback"
        @open-in-web="openInWeb"
        @notify="notify"
        @open-lightbox="configStore.setImageUrl"
        @empty-view="fixEmptyView"
        @copy-backlink-url="copyLink"
      />
    </div>
  </InnerView>
</template>

<script setup lang="ts">
import InnerView from '@commons/components/Layout/InnerView.vue'
import KnowledgeOptions from '@commons/components/Layout/KnowledgeOptions.vue'
import { useFavoritesStore, useKnowledgeStore } from '@getmayday/vue-modules'
import useScreen from '@commons/composables/useScreen'
import sanitizeHTML from '@commons/utils/sanitizeHTML'

const knowledgeStore = useKnowledgeStore()
const configStore = useConfigStore()
const iadvizeStore = useHelpdeskStore()
const { itemIsFavorite } = useFavoritesStore()

const { content } = storeToRefs(knowledgeStore)

const { goToEntity, toggleFavorite, getConfig, goToFeedback, openInWeb, modalIsOpen, copyLink } =
  useEntities()

const { notify } = useNotification()
const { fixEmptyView } = useApp()

const displayAiAction = computed(
  () =>
    content.value.type === 'Article' &&
    ((content.value.summary && content.value.summary.isVisible) ||
      (content.value.shortSummary && content.value.shortSummary.isVisible) ||
      (content.value.actionItems && content.value.actionItems.isVisible))
)

//If the content has both summary and action items, the height of the AI collapse is fixed (to avoid height jumps between tabs)
// otherwise we use the height of the visible element
const aiCollapseHeight = computed(() =>
  ((content.value.summary && content.value.summary.isVisible) ||
    (content.value.shortSummary && content.value.shortSummary.isVisible)) &&
  content.value.actionItems &&
  content.value.actionItems.isVisible
    ? { open: 'h-40', close: 'h-0' }
    : { open: 'max-h-40', close: 'max-h-0' }
)

const contentSummary = computed(() => {
  if (content.value.summary && content.value.summary.isVisible) {
    return { ...content.value.summary, type: 'detailed_summary' }
  }
  if (content.value.shortSummary && content.value.shortSummary.isVisible) {
    return { ...content.value.shortSummary, type: 'short_summary' }
  }
  return null
})

const contentActionItems = computed(() => {
  if (content.value.actionItems && content.value.actionItems.isVisible) {
    return content.value.actionItems
  }
  return null
})

const displayAiCollapse = ref(false)

const handleEntity = (entity: any) => {
  if (entity.type === 'Answer') iadvizeStore.injectAnswer(sanitizeHTML(entity.body))
  goToEntity(entity)
}

const customWidth = computed(() => useScreen().screenWidth.value - 20)
</script>

<style scoped>
:deep(.mayday-editor__inline-content__modal) {
  display: none;
}
</style>
